import { useRef } from "react";

/**
 * At most one request in flight per stream, latest scope wins.
 *
 * Every pivot request at head-office scope is a 15–60s query that holds two of the backend's
 * ten pooled connections, and the server keeps running it after the browser stops caring —
 * neither the effect's `cancelled` flag nor an aborted fetch reaches Postgres. On 2026-10-21
 * one user ticking 14 ประเภทร้าน boxes, one a second, put 14 of them on the server at once:
 * the pool drained and every screen in FE and PWA timed out for ~13 minutes. A debounce alone
 * does not bound that (the clicks were 1–3s apart), so the requests are queued as well.
 *
 * `run(isStale, request)` waits until the stream's previous request has settled, then fires
 * `request` only if its effect has not been superseded meanwhile — so however many changes
 * pile up behind a slow query, exactly one (the last) goes out after it. Resolves to `null`
 * when skipped; the caller treats that like a cancelled effect.
 */
export function createSingleFlight() {
  let inflight: Promise<void> | null = null;

  return async function run<T>(
    isStale: () => boolean,
    request: () => Promise<T>,
  ): Promise<T | null> {
    while (inflight) {
      await inflight;
      if (isStale()) return null;
    }
    if (isStale()) return null;

    const pending = request();
    // Settles either way and never rejects, so waiters need no catch of their own.
    const tracked: Promise<void> = pending.then(
      () => undefined,
      () => undefined,
    );
    inflight = tracked;
    tracked.then(() => {
      if (inflight === tracked) inflight = null;
    });
    return pending;
  };
}

/** One single-flight stream that lives as long as the component. */
export function usePivotSingleFlight() {
  const ref = useRef<ReturnType<typeof createSingleFlight> | null>(null);
  if (!ref.current) ref.current = createSingleFlight();
  return ref.current;
}

/**
 * How long the multi-select filters (ประเภทร้าน, กลุ่มร้าน, สินค้า) sit still before the page
 * queries them. Long enough to tick several boxes in a row as one change.
 */
export const PIVOT_FILTER_DEBOUNCE_MS = 800;
